import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormField } from './FormField';
import { Button } from '@/components/ui/Button';
import { ThankYouModal } from '@/components/ui/ThankYouModal';
import { cn } from '@/lib/cn';

const SERVICE_LABELS = {
  'logo-design': 'Logo design',
  'website-design': 'Website design',
  'website-development': 'Website development',
  bundle: 'Bundle',
  other: 'Something else',
} as const;

const BUDGET_LABELS = {
  '<1k': 'Under €1,000',
  '1-3k': '€1,000 - €3,000',
  '3-6k': '€3,000 - €6,000',
  '6-12k': '€6,000 - €12,000',
  '12k+': '€12,000 and up',
  custom: 'Prefer to discuss',
} as const;

const schema = z.object({
  name: z.string().min(2, 'Minimum two characters'),
  email: z.string().email('Not a valid email'),
  phone: z.string().regex(/^[+\d\s()-]*$/, 'Digits, spaces, +, -, () only').optional().or(z.literal('')),
  service: z.enum(['logo-design', 'website-design', 'website-development', 'bundle', 'other'], {
    message: 'Pick a service',
  }),
  budget: z.enum(['<1k', '1-3k', '3-6k', '6-12k', '12k+', 'custom']).optional(),
  message: z.string().min(10, 'At least ten characters').max(2000, 'Up to two thousand characters'),
  botcheck: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

const inputBase =
  'w-full bg-transparent border border-border px-4 py-3 text-ink-primary font-sans focus:border-accent focus:outline-none transition-colors';

const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';
const ACCESS_KEY = import.meta.env.VITE_WEB3FORMS_ACCESS_KEY;

export function ContactForm() {
  const [open, setOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { service: undefined, budget: undefined },
  });

  const onSubmit = async (data: FormValues) => {
    setSubmitError(null);

    if (data.botcheck) {
      return;
    }

    const serviceLabel = SERVICE_LABELS[data.service];
    const budgetLabel = data.budget ? BUDGET_LABELS[data.budget] : 'Not specified';

    const payload = {
      access_key: ACCESS_KEY,
      from_name: 'JUST SEE LTD Website',
      subject: `New brief from ${data.name} - ${serviceLabel}`,
      replyto: data.email,
      botcheck: '',
      name: data.name,
      email: data.email,
      phone: data.phone || 'Not provided',
      service: serviceLabel,
      budget: budgetLabel,
      message: data.message,
    };

    try {
      const response = await fetch(WEB3FORMS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (result.success) {
        setOpen(true);
        reset();
      } else {
        setSubmitError(result.message || 'Something went wrong. Please try again or email us directly.');
      }
    } catch {
      setSubmitError('Network error. Please try again or email us directly.');
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6 max-w-2xl">
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="hidden"
          style={{ display: 'none' }}
          {...register('botcheck')}
        />
        <div className="grid md:grid-cols-2 gap-6">
          <FormField label="Name" htmlFor="name" required error={errors.name?.message}>
            <input id="name" type="text" autoComplete="name" className={inputBase} {...register('name')} />
          </FormField>
          <FormField label="Email" htmlFor="email" required error={errors.email?.message}>
            <input id="email" type="email" autoComplete="email" className={inputBase} {...register('email')} />
          </FormField>
        </div>
        <FormField label="Phone (optional)" htmlFor="phone" error={errors.phone?.message}>
          <input id="phone" type="tel" autoComplete="tel" className={inputBase} {...register('phone')} />
        </FormField>
        <div className="grid md:grid-cols-2 gap-6">
          <FormField label="Service" htmlFor="service" required error={errors.service?.message}>
            <select id="service" className={cn(inputBase, 'appearance-none')} defaultValue="" {...register('service')}>
              <option value="" disabled>Select one</option>
              <option value="logo-design">Logo design</option>
              <option value="website-design">Website design</option>
              <option value="website-development">Website development</option>
              <option value="bundle">A bundle</option>
              <option value="other">Something else</option>
            </select>
          </FormField>
          <FormField label="Budget range (optional)" htmlFor="budget" error={errors.budget?.message}>
            <select id="budget" className={cn(inputBase, 'appearance-none')} defaultValue="" {...register('budget')}>
              <option value="">No preference</option>
              <option value="<1k">Under €1,000</option>
              <option value="1-3k">€1,000 - €3,000</option>
              <option value="3-6k">€3,000 - €6,000</option>
              <option value="6-12k">€6,000 - €12,000</option>
              <option value="12k+">€12,000 and up</option>
              <option value="custom">Prefer to discuss</option>
            </select>
          </FormField>
        </div>
        <FormField
          label="What do you need?"
          htmlFor="message"
          required
          hint="A sentence about what you do and roughly when you want to launch is enough."
          error={errors.message?.message}
        >
          <textarea id="message" rows={6} className={cn(inputBase, 'resize-y min-h-[140px]')} {...register('message')} />
        </FormField>
        {submitError && (
          <p role="alert" className="text-sm text-red-500">
            {submitError}
          </p>
        )}
        <div className="pt-2">
          <Button type="submit" variant="primary" withArrow disabled={isSubmitting}>
            {isSubmitting ? 'Sending' : 'Send message'}
          </Button>
        </div>
      </form>
      <ThankYouModal open={open} onOpenChange={setOpen} />
    </>
  );
}
